import {
  trackBulkActionFailure,
  trackBulkActionSuccess,
  trackUserBaseV2Selector,
} from '@fullstory/analytics';
import { intl } from '@fullstory/intl';
import {
  BulkArchiveObjectsResponse,
  BulkDereferenceObjectsRequest,
  BulkOwnObjectsRequest,
  BulkReferenceObjectsRequest,
  DereferenceObjectRequest,
  ObjectType,
  ReferenceObjectRequest,
} from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useNotifications } from '@fullstory/react-hooks';
import { DashboardRequestActions, SpaceRequestActions } from '@fullstory/redux';
import { BulkRequestActions } from '@fullstory/redux/modules/bulkActions/actions';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { AppDispatch } from '@fullstory/redux/types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export interface BulkActionIds {
  funnels: string[];
  heatmaps: string[];
  journeys: string[];
  metrics: string[];
  retentionMetrics: string[];
  segments: string[];
  dashboards: string[];
  dataTables: string[];
}

export const useLibraryBulkActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const userAnalytics = useSelector(trackUserBaseV2Selector);

  const archiveFunc = useCallback(
    async (ids: BulkActionIds, archived: boolean, itemsSelected: number) => {
      const action = archived ? 'archive' : 'restore';
      try {
        const rsp = await dispatch(
          BulkRequestActions.bulkArchiveObjects({
            orgId,
            funnels: ids.funnels.length
              ? {
                  orgId,
                  funnels: ids.funnels.map(id => ({ orgId, funnelId: id, archived })),
                }
              : null,
            heatmaps: ids.heatmaps.length
              ? {
                  orgId,
                  heatmaps: ids.heatmaps.map(id => ({ orgId, heatmapId: id, archived })),
                }
              : null,
            journeys: ids.journeys.length
              ? {
                  orgId,
                  journeys: ids.journeys.map(id => ({ orgId, journeyId: id, archived })),
                }
              : null,
            metrics: ids.metrics.length
              ? {
                  orgId,
                  metrics: ids.metrics.map(id => ({ orgId, metricId: id, archived })),
                }
              : null,
            retentionMetrics: ids.retentionMetrics.length
              ? {
                  orgId,
                  retentionMetrics: ids.retentionMetrics.map(id => ({
                    orgId,
                    retentionMetricId: id,
                    archived,
                  })),
                }
              : null,
            segments: ids.segments.length
              ? {
                  orgId,
                  segments: ids.segments.map(id => ({ orgId, segmentId: id, archived })),
                }
              : null,
            dataTables: ids.dataTables.length
              ? {
                  orgId,
                  dataTables: ids.dataTables.map(id => ({ orgId, dataTableId: id, archived })),
                }
              : null,
          }),
        );
        trackBulkActionSuccess({
          ...userAnalytics,
          action,
          itemsSelected,
          itemsActioned: getTotalItemsArchived(rsp),
        });
        return rsp;
      } catch (e) {
        trackBulkActionFailure({ ...userAnalytics, action, itemsSelected });
        return null;
      }
    },
    [dispatch, orgId, userAnalytics],
  );

  const bulkArchive = useCallback(
    async (ids: BulkActionIds, itemsSelected: number) => {
      try {
        const rsp = await archiveFunc(ids, true, itemsSelected);
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Archived items.', id: 'ZBhThz' }),
        );
        return rsp;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive items. Please try again.',
            id: 'Q+WL7b',
          }),
        );
        return null;
      }
    },
    [notifications, archiveFunc],
  );

  const bulkRestore = useCallback(
    async (ids: BulkActionIds, itemsSelected: number) => {
      try {
        const rsp = await archiveFunc(ids, false, itemsSelected);
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Restored items.', id: '+QXiL6' }),
        );
        return rsp;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({
            defaultMessage: 'Failed to restore items. Please try again.',
            id: 'qf01Pm',
          }),
        );
        return null;
      }
    },
    [notifications, archiveFunc],
  );

  const bulkTransfer = useCallback(
    async (ids: BulkActionIds, spaceId: string, keepReference: boolean) => {
      const req: BulkOwnObjectsRequest = {
        orgId,
        objects: [],
      };
      ids.dashboards.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.DASHBOARD,
          keepReference,
        }),
      );
      ids.funnels.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.FUNNEL,
          keepReference,
        }),
      );
      ids.heatmaps.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.HEATMAP,
          keepReference,
        }),
      );
      ids.journeys.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.JOURNEY,
          keepReference,
        }),
      );
      ids.metrics.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.METRIC,
          keepReference,
        }),
      );
      ids.retentionMetrics.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.RETENTION,
          keepReference,
        }),
      );
      ids.segments.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.SEGMENT,
          keepReference,
        }),
      );
      ids.dataTables.forEach(id =>
        req.objects.push({
          orgId,
          spaceId,
          dxObjectId: id,
          dxObjectType: ObjectType.DATA_TABLE,
          keepReference,
        }),
      );
      try {
        const rsp = await dispatch(SpaceRequestActions.moveObjectsToSpace(req));
        trackBulkActionSuccess({
          ...userAnalytics,
          action: 'transfer',
          itemsSelected: req.objects.length,
          itemsActioned: rsp.objects.length,
        });
        notifications.success(
          intl.formatMessage({ defaultMessage: 'Successfully transfered items.', id: 'jlFqMx' }),
        );
        return rsp;
      } catch (e) {
        trackBulkActionFailure({
          ...userAnalytics,
          action: 'transfer',
          itemsSelected: req.objects.length,
        });
        return null;
      }
    },
    [dispatch, notifications, orgId, userAnalytics],
  );

  const bulkReference = useCallback(
    async (ids: BulkActionIds, spaceId: string) => {
      const req: BulkReferenceObjectsRequest = {
        orgId,
        objects: getReferenceObjects(orgId, ids, spaceId),
      };
      try {
        if (req.objects.length < 1) {
          notifications.info(
            intl.formatMessage({ defaultMessage: 'No items to add to space.', id: '0YJJ7P' }),
          );
          return null;
        }
        const rsp = await dispatch(SpaceRequestActions.bulkLinkObjectsToSpace(req));
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Items added to space.', id: 'clzhVf' }),
        );
        trackBulkActionSuccess({
          ...userAnalytics,
          action: 'add',
          itemsSelected: req.objects.length,
          itemsActioned: rsp.objects.length,
        });
        return rsp;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({
            defaultMessage: 'Failed to add items to space. Please try again.',
            id: 'HRORzW',
          }),
        );
        trackBulkActionFailure({
          ...userAnalytics,
          action: 'add',
          itemsSelected: req.objects.length,
        });
        return null;
      }
    },
    [orgId, dispatch, notifications, userAnalytics],
  );

  const bulkDereference = useCallback(
    async (ids: BulkActionIds, spaceId: string) => {
      const req: BulkDereferenceObjectsRequest = {
        orgId,
        objects: getReferenceObjects(orgId, ids, spaceId),
      };
      try {
        if (req.objects.length < 1) {
          notifications.info(
            intl.formatMessage({ defaultMessage: 'No items to remove from space.', id: '7zta8z' }),
          );
          return null;
        }
        const rsp = await dispatch(SpaceRequestActions.bulkDereferenceObject(req));
        trackBulkActionSuccess({
          ...userAnalytics,
          action: 'remove',
          itemsSelected: req.objects.length,
          itemsActioned: req.objects.length,
        });
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Items removed from space.', id: 'Ts0XKk' }),
        );
        return rsp;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({
            defaultMessage: 'Failed to remove items from space. Please try again.',
            id: '9VongP',
          }),
        );
        trackBulkActionFailure({
          ...userAnalytics,
          action: 'remove',
          itemsSelected: req.objects.length,
        });
        return null;
      }
    },
    [dispatch, notifications, orgId, userAnalytics],
  );

  const bulkDeleteDashboards = useCallback(
    async (ids: int64[], itemsSelected: number) => {
      try {
        // attempt
        const rsp = await dispatch(DashboardRequestActions.bulkDeleteDashboards(ids, orgId));
        notifications.success(
          intl.formatMessage({ defaultMessage: 'Successfully deleted dashboards.', id: 'NMy737' }),
        );
        trackBulkActionSuccess({
          ...userAnalytics,
          action: 'delete',
          itemsSelected,
          itemsActioned: ids.length,
        });
        return rsp;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to delete dashboards. Please try again.',
            id: 'CzRqJD',
          }),
        );
        trackBulkActionFailure({ ...userAnalytics, action: 'delete', itemsSelected });
        return null;
      }
    },
    [dispatch, notifications, orgId, userAnalytics],
  );

  return {
    bulkArchive,
    bulkRestore,
    bulkTransfer,
    bulkReference,
    bulkDereference,
    bulkDeleteDashboards,
  };
};

const getReferenceObjects = (
  orgId: string,
  ids: BulkActionIds,
  spaceId: string,
): ReferenceObjectRequest[] | DereferenceObjectRequest[] => {
  const objects: ReferenceObjectRequest[] | DereferenceObjectRequest[] = [];

  ids.dashboards.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.DASHBOARD,
    }),
  );
  ids.funnels.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.FUNNEL,
    }),
  );
  ids.heatmaps.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.HEATMAP,
    }),
  );
  ids.journeys.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.JOURNEY,
    }),
  );
  ids.metrics.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.METRIC,
    }),
  );
  ids.retentionMetrics.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.RETENTION,
    }),
  );
  ids.segments.forEach(id =>
    objects.push({
      orgId,
      spaceId,
      dxObjectId: id,
      dxObjectType: ObjectType.SEGMENT,
    }),
  );

  return objects;
};

const getTotalItemsArchived = (rsp: BulkArchiveObjectsResponse) =>
  (rsp.funnels?.funnels?.length ?? 0) +
  (rsp.heatmaps?.heatmaps?.length ?? 0) +
  (rsp.journeys?.journeys?.length ?? 0) +
  (rsp.metrics?.metrics?.length ?? 0) +
  (rsp.retentionMetrics?.retentionMetrics?.length ?? 0) +
  (rsp.segments?.segments?.length ?? 0);
